// Kiri's conversational action cards — navigate / find / summary / job
// draft / task / diary / pending-confirmation / denied / error. Designed to
// feel like part of the conversation (soft surfaces, minimal borders) rather
// than a developer console response.
import { ArrowUpRight, Search, Briefcase, BookOpenText, Check, RotateCcw, TrendingDown, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`mt-2 bg-[#12161C] rounded-xl border border-white/5 overflow-hidden ${className}`} {...rest}>{children}</div>
);

const CardBody = ({ children }) => <div className="px-3.5 py-3">{children}</div>;

const OpenLink = ({ onClick, label = "OPEN", testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group mt-2 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#FF7A3D] hover:text-[#FF8F5C] transition-colors"
  >
    {label} <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </button>
);

export function DeniedCard({ text }) {
  return <Card><CardBody><p className="text-xs text-zinc-400">{text}</p></CardBody></Card>;
}

export function NavigateCard({ data, onGo }) {
  return (
    <Card data-testid="kiri-navigate-btn">
      <CardBody>
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          <ArrowUpRight className="w-4 h-4 text-[#FF7A3D]" /> {data.label.toUpperCase()}
        </div>
        <p className="text-xs text-zinc-500 mt-0.5">Jump straight there.</p>
        <OpenLink onClick={onGo} testId="kiri-navigate-btn" />
      </CardBody>
    </Card>
  );
}

export function FindCard({ isJob, items, onGo }) {
  return (
    <Card>
      <div className="flex items-center gap-2 px-3.5 pt-3 text-white font-semibold text-sm">
        <Search className="w-4 h-4 text-[#FF7A3D]" /> {isJob ? "Jobs found" : "Clients found"}
      </div>
      {items.length === 0 ? (
        <CardBody><p className="text-xs text-zinc-500">No matches found.</p></CardBody>
      ) : (
        <div className="mt-1.5 divide-y divide-white/5">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => onGo(it)}
              className="w-full text-left px-3.5 py-2.5 hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-2"
              data-testid={`kiri-find-result-${it.id}`}
            >
              <span className="text-sm text-white truncate">{isJob ? it.title : it.name}</span>
              {isJob && <span className="text-[10px] font-mono text-zinc-500 shrink-0">{it.job_number}</span>}
            </button>
          ))}
          <div className="h-1" />
        </div>
      )}
    </Card>
  );
}

export function SummaryCard({ data, onOpen }) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          <Briefcase className="w-4 h-4 text-[#FF7A3D]" /> JOB SUMMARY
        </div>
        <div className="text-sm font-bold text-white mt-1.5">{data.job_number} {data.title}</div>
        <div className="text-xs text-zinc-400 mt-1 space-y-0.5">
          <div>{data.status} · {data.client_name || "no client set"}</div>
          <div>{data.done_tasks} of {data.done_tasks + data.open_tasks} tasks done · {data.open_list_items} open list items</div>
          {!!data.variations_pending && <div>{data.variations_pending} variation(s) awaiting price</div>}
          <div>Last diary: {data.last_diary_date || "none yet"}</div>
        </div>
        <OpenLink onClick={onOpen} label="VIEW DETAILS" testId="kiri-summary-open" />
      </CardBody>
    </Card>
  );
}

export function JobDraftCard({ data, onOpen }) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          <Briefcase className="w-4 h-4 text-[#FF7A3D]" /> JOB DRAFT
        </div>
        <div className="text-xs text-zinc-400 mt-1.5 space-y-0.5">
          {data.title && <div><span className="text-zinc-500">Title </span><span className="text-white">{data.title}</span></div>}
          {data.address && <div><span className="text-zinc-500">Address </span>{data.address}</div>}
          {data.scope && <div><span className="text-zinc-500">Scope </span>{data.scope}</div>}
        </div>
        <OpenLink onClick={onOpen} label="REVIEW IN CREATE JOB" testId="kiri-open-job-create-btn" />
      </CardBody>
    </Card>
  );
}

export function TaskCreatedCard({ data }) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-2 text-emerald-400 font-semibold text-sm">
          <Check className="w-4 h-4" /> DONE
        </div>
        <p className="text-sm text-white mt-1">Task added: {data.text}</p>
      </CardBody>
    </Card>
  );
}

export function DiaryCreatedCard({ data, onOpen }) {
  return (
    <Card>
      <div className="flex items-center gap-2 px-3.5 pt-3 text-emerald-400 font-semibold text-sm">
        <BookOpenText className="w-4 h-4" /> SITE DIARY — DRAFT
      </div>
      <div className="px-3.5 py-2.5 text-xs text-zinc-300 whitespace-pre-wrap max-h-40 overflow-y-auto leading-relaxed">
        {data.content}
      </div>
      <div className="px-3.5 pb-3">
        <OpenLink onClick={onOpen} label="REVIEW DRAFT" testId="kiri-diary-open" />
      </div>
    </Card>
  );
}

export function PendingCard({ action, onCancel, onConfirm, busy }) {
  return (
    <Card className="border-[#FF5F15]/25">
      <CardBody>
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Confirm to continue</div>
        <div className="text-sm font-bold text-white mt-1">{action.label}</div>
        <div className="mt-2 space-y-1.5">
          {Object.entries(action.params || {}).map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">{k.replace(/_/g, " ")}</div>
              <div className="text-sm text-zinc-200">{String(v)}</div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-3">
          <Button size="sm" variant="outline" onClick={onCancel} className="rounded-lg border-zinc-700 bg-transparent text-zinc-300 hover:text-white hover:bg-white/5 h-8 text-xs flex-1" data-testid="kiri-cancel-action-btn">
            Change
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={busy} className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white h-8 text-xs flex-1" data-testid="kiri-confirm-action-btn">
            {busy ? "Working…" : "Confirm"}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export function CancelledNote() {
  return <div className="mt-2 text-[11px] text-zinc-500 italic pl-1">Cancelled — nothing was changed.</div>;
}

export function ErrorCard({ onRetry }) {
  return (
    <Card>
      <CardBody>
        <p className="text-sm text-zinc-200">I couldn't complete that. Nothing in Fieldlink was changed.</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#FF7A3D] hover:text-[#FF8F5C] transition-colors"
          data-testid="kiri-retry-btn"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Try again
        </button>
      </CardBody>
    </Card>
  );
}

export function FollowUpChips({ chips, onPick }) {
  if (!chips || chips.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {chips.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          className="text-[11px] bg-transparent border border-zinc-700 hover:border-[#FF5F15] text-zinc-400 hover:text-white px-2.5 py-1 rounded-full transition-colors"
          data-testid="kiri-followup-chip"
        >
          {c}
        </button>
      ))}
    </div>
  );
}



const marginColor = (m) =>
  m === null || m === undefined ? "text-zinc-400"
    : m < 0 ? "text-red-400" : m < 10 ? "text-amber-400" : "text-emerald-400";

export function InsightsCard({ data, onOpenJob }) {
  if (!data) return null;
  const { headline, stats, jobs, unbilled, lines, note } = data;
  return (
    <Card data-testid="kiri-insights-card">
      <CardBody>
        <div className="flex items-center gap-2 text-white font-semibold text-sm">
          <Wallet className="w-4 h-4 text-[#FF7A3D]" /> {headline}
        </div>

        {Array.isArray(stats) && stats.length > 0 && (
          <div className="mt-2.5 grid grid-cols-2 gap-2">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg bg-white/[0.04] px-2.5 py-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">{s.label}</div>
                <div className="text-sm font-bold text-white mt-0.5">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {Array.isArray(jobs) && jobs.length > 0 && (
          <div className="mt-2.5 divide-y divide-white/5 rounded-lg bg-white/[0.03]">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => onOpenJob?.(j.id)}
                className="w-full text-left px-3 py-2 hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-2"
                data-testid={`kiri-insight-job-${j.id}`}>
                <span className="min-w-0">
                  <span className="block text-sm text-white truncate">{j.title}</span>
                  <span className="block text-[10px] font-mono text-zinc-500">
                    {j.job_number || ""} · rev {typeof j.revenue === "number" ? `$${Math.round(j.revenue).toLocaleString()}` : "—"}
                  </span>
                </span>
                <span className={`text-sm font-bold shrink-0 flex items-center gap-1 ${marginColor(j.margin)}`}>
                  {j.margin === null || j.margin === undefined ? "—" : `${j.margin}%`}
                  {typeof j.margin === "number" && j.margin < 10 && <TrendingDown className="w-3.5 h-3.5" />}
                </span>
              </button>
            ))}
          </div>
        )}

        {Array.isArray(unbilled) && unbilled.length > 0 && (
          <div className="mt-2.5 divide-y divide-white/5 rounded-lg bg-white/[0.03]">
            {unbilled.map((u) => (
              <button key={u.id} onClick={() => onOpenJob?.(u.id)}
                className="w-full text-left px-3 py-2 hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-2"
                data-testid={`kiri-insight-unbilled-${u.id}`}>
                <span className="text-sm text-white truncate">{u.title}</span>
                <span className="text-sm font-bold text-amber-400 shrink-0">${Math.round(u.amount).toLocaleString()}</span>
              </button>
            ))}
          </div>
        )}

        {Array.isArray(lines) && lines.length > 0 && (
          <ul className="mt-2.5 space-y-1">
            {lines.map((l, i) => <li key={i} className="text-xs text-zinc-300 leading-relaxed">{l}</li>)}
          </ul>
        )}

        {note && <p className="mt-2 text-xs text-emerald-400/90">{note}</p>}
      </CardBody>
    </Card>
  );
}

export function CreatedCard({ label, message, onOpen, openLabel = "OPEN", testId }) {
  return (
    <Card data-testid={testId || "kiri-created-card"}>
      <CardBody>
        <div className="flex items-center gap-2 text-emerald-400 font-semibold text-sm">
          <Check className="w-4 h-4" /> {label}
        </div>
        {message && <p className="text-sm text-white mt-1">{message}</p>}
        {onOpen && <OpenLink onClick={onOpen} label={openLabel} testId={testId ? `${testId}-open` : "kiri-created-open"} />}
      </CardBody>
    </Card>
  );
}
